/**
 * AdminScreen.tsx
 * @version 2.1.0 — + onglet Zones photo (PhotoSlotsManager)
 */
import { useEffect, useState } from 'react'
import {
  Shield, UserPlus, Trash2, ArrowLeft,
  Loader, CheckCircle, AlertCircle, RefreshCw
} from 'lucide-react'
import { useAdminStore }      from '../store/useAdminStore'
import { useSettingsStore }   from '../store/useSettingsStore'
import { useAppStore }        from '../store/useAppStore'
import { PhotoSlotsManager }  from './PhotoSlotsManager'
import { ChecklistManager }   from './ChecklistManager'
import { DamageManager }      from './DamageManager'
import { buildAuthHeader }    from '../utils/auth'
import toast from 'react-hot-toast'

// ── Types ─────────────────────────────────────────────────────────────────
type Tab = 'admins' | 'settings' | 'slots' | 'checklist' | 'damages'

const TAB_LABELS: Record<Tab, string> = {
  admins:   '👥 Admins',
  settings: '⚙️ Paramètres',
  slots:    '📸 Zones photo',
  checklist: '📋 Checklist',
  damages:  '💥 Dommages',
}

// ── Composant principal ───────────────────────────────────────────────────
export default function AdminScreen({ onBack }: { onBack: () => void }) {
  const { credentials } = useAppStore()
  const token = credentials
    ? buildAuthHeader(credentials)
    : ''

  const { users, loading, error, fetchUsers, addUser, removeUser } = useAdminStore()
  const { settings, fetchSettings, updateSettings, resetSettings } = useSettingsStore()

  const [newUsername, setNewUsername]   = useState('')
  const [activeTab, setActiveTab]       = useState<Tab>('admins')   // ✅ type Tab
  const [settingsForm, setSettingsForm] = useState<Record<string, string>>({})
  const [saving, setSaving]             = useState(false)

  useEffect(() => {
    fetchUsers(token)
    fetchSettings(token)
  }, [])

  useEffect(() => {
    setSettingsForm({
      photo_mrc_quality:       String(settings.photo_mrc_quality),
      photo_supabase_quality:  String(settings.photo_supabase_quality),
      photo_supabase_max_dim:  String(settings.photo_supabase_max_dim),
      upload_timeout_ms:       String(settings.upload_timeout_ms),
      upload_max_retries:      String(settings.upload_max_retries),
      upload_poll_interval_ms: String(settings.upload_poll_interval_ms),
      pdf_in_email:            String(settings.pdf_in_email),
      email_enabled:           String(settings.email_enabled),
      theme_mode:              String(settings.theme_mode),
      theme_glass:             String(settings.theme_glass),
      theme_color:             String(settings.theme_color),
    })
  }, [settings])

  const handleAddUser = async () => {
    if (!newUsername.trim()) return toast.error('Entrez un identifiant')
    try {
      await addUser(token, newUsername.trim())
      setNewUsername('')
      toast.success(`${newUsername.trim()} ajouté comme admin ✅`)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const handleRemoveUser = async (username: string) => {
    if (!confirm(`Supprimer les droits admin de ${username} ?`)) return
    try {
      await removeUser(token, username)
      toast.success(`${username} retiré des admins`)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const handleSaveSettings = async () => {
    setSaving(true)
    try {
      await updateSettings(token, settingsForm)
      toast.success('Paramètres sauvegardés ✅')
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleResetSettings = async () => {
    if (!confirm('Remettre tous les paramètres aux valeurs par défaut ?')) return
    try {
      await resetSettings(token)
      toast.success('Paramètres réinitialisés ✅')
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Header ── */}
      <div className="bg-gradient-to-r from-indigo-700 to-blue-600 text-white px-4 py-4 flex items-center gap-3 shadow-lg">
        <button onClick={onBack} className="p-2 rounded-xl hover:bg-white/20 transition">
          <ArrowLeft size={22} />
        </button>
        <Shield size={24} />
        <div>
          <h1 className="font-bold text-lg leading-tight">Administration</h1>
          <p className="text-blue-200 text-xs">Gestion des accès et paramètres</p>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex border-b border-gray-200 bg-white sticky top-0 z-10 shadow-sm">
        {(Object.keys(TAB_LABELS) as Tab[]).map(tab => (   // ✅ 3 onglets via TAB_LABELS
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 text-xs font-semibold transition ${
              activeTab === tab
                ? 'text-indigo-600 border-b-2 border-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {TAB_LABELS[tab]}
          </button>
        ))}
      </div>

      <div className="p-4 max-w-lg mx-auto">

        {/* ── TAB ADMINS ── */}
        {activeTab === 'admins' && (
          <div className="flex flex-col gap-4 mt-2">

            {/* Ajouter un admin */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <h2 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <UserPlus size={18} className="text-indigo-500" />
                Ajouter un administrateur
              </h2>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newUsername}
                  onChange={e => setNewUsername(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddUser()}
                  placeholder="Identifiant myrentcar"
                  className="flex-1 border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none"
                />
                <button
                  onClick={handleAddUser}
                  disabled={loading}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition"
                >
                  {loading ? <Loader size={16} className="animate-spin" /> : 'Ajouter'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                L'identifiant doit correspondre exactement au login myrentcar de l'utilisateur.
              </p>
            </div>

            {/* Liste des admins */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-gray-800 flex items-center gap-2">
                  <Shield size={18} className="text-indigo-500" />
                  Administrateurs actifs
                  <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full font-bold">
                    {users.length}
                  </span>
                </h2>
                <button
                  onClick={() => fetchUsers(token)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition"
                >
                  <RefreshCw size={16} />
                </button>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-xl p-3 mb-3">
                  <AlertCircle size={16} /> {error}
                </div>
              )}

              {loading && !users.length ? (
                <div className="flex justify-center py-6">
                  <Loader size={24} className="animate-spin text-indigo-400" />
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {users.map(u => (
                    <div
                      key={u.username}
                      className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2.5"
                    >
                      <div>
                        <p className="font-semibold text-gray-800 text-sm flex items-center gap-1.5">
                          <CheckCircle size={14} className="text-green-500" />
                          {u.username}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Ajouté par {u.added_by} · {new Date(u.added_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveUser(u.username)}
                        className="p-2 rounded-lg hover:bg-red-100 text-gray-400 hover:text-red-500 transition"
                        title="Retirer les droits admin"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TAB SETTINGS ── */}
        {activeTab === 'settings' && (
          <div className="flex flex-col gap-4 mt-2">

            {/* Qualité photos */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <h2 className="font-bold text-gray-700 mb-3 text-sm uppercase tracking-wide">📷 Photos</h2>
              <div className="flex flex-col gap-3">
                <SettingRow
                  label="Qualité MRC (compression)"
                  hint="0.1 = très compressé · 1.0 = original"
                  value={settingsForm.photo_mrc_quality || ''}
                  onChange={v => setSettingsForm(f => ({ ...f, photo_mrc_quality: v }))}
                />
                <SettingRow
                  label="Qualité Supabase Storage"
                  hint="0.1 à 1.0"
                  value={settingsForm.photo_supabase_quality || ''}
                  onChange={v => setSettingsForm(f => ({ ...f, photo_supabase_quality: v }))}
                />
                <SettingRow
                  label="Dimension max Supabase (px)"
                  hint="Ex: 2048"
                  value={settingsForm.photo_supabase_max_dim || ''}
                  onChange={v => setSettingsForm(f => ({ ...f, photo_supabase_max_dim: v }))}
                />
              </div>
            </div>

            {/* Upload */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <h2 className="font-bold text-gray-700 mb-3 text-sm uppercase tracking-wide">⬆️ Upload</h2>
              <div className="flex flex-col gap-3">
                <SettingRow
                  label="Timeout (ms)"
                  hint="Ex: 120000 = 2 min"
                  value={settingsForm.upload_timeout_ms || ''}
                  onChange={v => setSettingsForm(f => ({ ...f, upload_timeout_ms: v }))}
                />
                <SettingRow
                  label="Tentatives max"
                  hint="Ex: 5"
                  value={settingsForm.upload_max_retries || ''}
                  onChange={v => setSettingsForm(f => ({ ...f, upload_max_retries: v }))}
                />
                <SettingRow
                  label="Intervalle polling (ms)"
                  hint="Ex: 5000 = 5s"
                  value={settingsForm.upload_poll_interval_ms || ''}
                  onChange={v => setSettingsForm(f => ({ ...f, upload_poll_interval_ms: v }))}
                />
              </div>
            </div>

            {/* Email / PDF */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <h2 className="font-bold text-gray-700 mb-3 text-sm uppercase tracking-wide">📧 Email & PDF</h2>
              <div className="flex flex-col gap-3">
                <ToggleRow
                  label="PDF joint dans l'email"
                  value={settingsForm.pdf_in_email === 'true'}
                  onChange={v => setSettingsForm(f => ({ ...f, pdf_in_email: String(v) }))}
                />
                <ToggleRow
                  label="Envoi email activé"
                  value={settingsForm.email_enabled === 'true'}
                  onChange={v => setSettingsForm(f => ({ ...f, email_enabled: String(v) }))}
                />
              </div>
            </div>

            {/* Apparence & Thème */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <h2 className="font-bold text-gray-700 mb-3 text-sm uppercase tracking-wide">🎨 Apparence</h2>
              <div className="flex flex-col gap-3">
                <SelectRow
                  label="Mode Sombre"
                  value={settingsForm.theme_mode || 'auto'}
                  options={[
                    { value: 'auto', label: 'Automatique (Appareil)' },
                    { value: 'light', label: 'Toujours Clair' },
                    { value: 'dark', label: 'Toujours Sombre' },
                  ]}
                  onChange={v => setSettingsForm(f => ({ ...f, theme_mode: v }))}
                />
                <SelectRow
                  label="Couleur Principale"
                  value={settingsForm.theme_color || 'indigo'}
                  options={[
                    { value: 'indigo', label: 'Indigo (Défaut)' },
                    { value: 'blue', label: 'Bleu Océan' },
                    { value: 'green', label: 'Vert Émeraude' },
                    { value: 'rose', label: 'Rose Rubis' },
                    { value: 'orange', label: 'Orange Soleil' },
                    { value: 'slate', label: 'Gris Carbone' },
                  ]}
                  onChange={v => setSettingsForm(f => ({ ...f, theme_color: v }))}
                />
                <ToggleRow
                  label="Effet Glassmorphism (Premium)"
                  value={settingsForm.theme_glass === 'true'}
                  onChange={v => setSettingsForm(f => ({ ...f, theme_glass: String(v) }))}
                />
              </div>
            </div>

            {/* Boutons */}
            <div className="flex gap-3">
              <button
                onClick={handleResetSettings}
                className="flex-1 border-2 border-gray-200 text-gray-600 py-3 rounded-xl font-semibold text-sm hover:bg-gray-50 transition"
              >
                Réinitialiser
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-indigo-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
              >
                {saving ? <Loader size={16} className="animate-spin" /> : null}
                Sauvegarder
              </button>
            </div>
          </div>
        )}

        {/* ── TAB SLOTS (Zones photo) ── */}
        {activeTab === 'slots' && (
          <div className="mt-2">
            <PhotoSlotsManager />
          </div>
        )}

        {/* ── TAB CHECKLIST ── */}
        {activeTab === 'checklist' && (
          <div className="mt-2">
            <ChecklistManager />
          </div>
        )}

        {/* ── TAB DOMMAGES ── */}
        {activeTab === 'damages' && (
          <div className="mt-2">
            <DamageManager />
          </div>
        )}

      </div>
    </div>
  )
}

// ── Composants internes ───────────────────────────────────────────────────
function SettingRow({
  label, hint, value, onChange
}: { label: string; hint: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none"
      />
      <p className="text-xs text-gray-400">{hint}</p>
    </div>
  )
}

function ToggleRow({
  label, value, onChange
}: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <button
        onClick={() => onChange(!value)}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          value ? 'bg-indigo-500' : 'bg-gray-300'
        }`}
      >
        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
          value ? 'translate-x-7' : 'translate-x-1'
        }`} />
      </button>
    </div>
  )
}

function SelectRow({
  label, value, options, onChange
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none bg-white"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  )
}